/**
 * REPORT-SCREENSHOT-MESSAGES.WIDGET.TSX
 * Preview of the slide - displays messages for a question
 */
import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import { Session } from "@/redux/_session.types"
import { Topic, TopicState } from "@/redux/topic.types"
import { useEffect, useState } from "react"
import { flatMap, orderBy } from "lodash"
import { Question } from "@/redux/question.types"
import { Page } from "@/redux/page.types"
import { Message } from "@/redux/message.types"
import { store } from "@/index"
import { fetchMessages } from "@/redux/_archive.actions"
import { FilterState } from "@/redux/filter.types"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import PageLoader from "@/components/page-loader"

interface StateProps {
  _session: Session
  topic: TopicState
  filter: FilterState
}

interface OwnProps {
  isFullWidth: boolean
  currentPage: Page
  currentTopic: Topic
}

type Props = StateProps & OwnProps & WithTranslation

// Number of messages to display per page
const MESSAGES_PER_PAGE = 10

function ReportScreenshotMessagesWidget(props: Props) {
  const { t } = props

  const [question] = useState<Question>(initQuestions())
  const [messages, setMessages] = useState<Message[]>([])
  const [status, setStatus] = useState(STATUS_LOADING)

  // By default, report message pages display the AI summary. Full comments are loaded only on explicit demand.
  const displayMessages =
    props.currentPage.contentLeft.screenshot.displayMessages ||
    props.currentPage.contentLeft.screenshot.summarize === false
  const pageIndex = props.currentPage.contentLeft.screenshot.pageIndex || 0

  function initQuestions() {
    let questions =
      props.currentTopic.Questions.length > 0
        ? props.currentTopic.Questions
        : flatMap(props.topic.list, (x) => x.Questions)
    questions = questions.filter((x) => x.type === "open")
    return questions.length > 0 ? questions[0] : new Question()
  }

  // Fetch messages when component mounts
  useEffect(() => {
    async function load() {
      if (question.aid && displayMessages) {
        setStatus(STATUS_LOADING)
        const lang = props._session.language

        const response: any = await store.dispatch(
          fetchMessages(
            props.filter.dashboard,
            question.aid,
            props.currentTopic.aid,
            undefined,
            undefined,
            lang,
            true // isReport = true
          )
        )

        if (!response.error) {
          setMessages(
            orderBy(
              response.map((x) => new Message(x)),
              "createdAt",
              "desc"
            )
          )
        }
        setStatus(STATUS_LOADED)
      }
    }

    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMessages, question.aid])

  // If summary mode, show only the help text
  if (!displayMessages) {
    return (
      <div>
        <div
          style={{
            fontSize: 18,
            marginBottom: 24
          }}>
          <b>{t("page_type_screenshot_messages_summarize_help", { question: question.label })}</b>
        </div>
      </div>
    )
  }

  // Calculate paginated messages
  const startIndex = pageIndex * MESSAGES_PER_PAGE
  const endIndex = startIndex + MESSAGES_PER_PAGE
  const paginatedMessages = messages.slice(startIndex, endIndex)

  return (
    <div>
      <div
        style={{
          fontSize: 18,
          marginBottom: 16,
          fontWeight: "bold"
        }}>
        {t("page_type_screenshot_messages_help", { question: question.label })}
      </div>

      {status === STATUS_LOADING ? (
        <PageLoader status={status} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {paginatedMessages.map((message, index) => (
            <div
              key={message.aid || index}
              style={{
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 6,
                fontSize: 14,
                lineHeight: 1.5
              }}>
              {message.text}
            </div>
          ))}

          {paginatedMessages.length === 0 && (
            <div style={{ textAlign: "center", color: "#999", padding: 20 }}>
              {t("message_no_data")}
            </div>
          )}

          {messages.length > MESSAGES_PER_PAGE && (
            <div style={{ textAlign: "right", fontSize: 12, color: "#666", marginTop: 8 }}>
              {startIndex + 1} - {Math.min(endIndex, messages.length)} / {messages.length}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  topic: state.topic,
  filter: state.filter
})

export default connect(mapStateToProps)(withTranslation()(ReportScreenshotMessagesWidget))
